<template>
    <div v-show="$store.getters['auth/isAuth'] === false">
        <div class="logo mb-4 text-center">
            <router-link :to="{ name: 'login' }">
                <img v-if="!isMobile" src="@/assets/lh-logo.png">
                <img v-else src="@/assets/lh-logo.svg">
            </router-link>
        </div>

        <ValidationObserver ref="obs" v-slot="{ invalid }" tag="form" class="mb-5" @submit.prevent="submit">
            <ValidationProvider key="recaptcha" tag="div" rules="required" vid="recaptcha">
                <recaptcha ref="recaptcha" v-model="recaptchaToken" @ready="recaptchaReady" />
            </ValidationProvider>

            <div v-if="!lastStep" class="lead mb-4 text-center">Восстановление пароля</div>

            <template v-if="step === 1">
                <ValidationProvider key="identifier" v-errors.api tag="div" class="form-group" vid="identifier" rules="required">
                    <label for="identifier">Телефон / почта</label>
                    <input id="identifier" v-model="identifier" v-focus type="text" class="form-control">
                </ValidationProvider>

                <small class="form-text text-muted">
                    На ваш email, указанный при регистрации, будет отправлено письмо с дальнейшей инструкцией.
                </small>
            </template>

            <template v-if="step === 2">
                <ValidationProvider key="code" v-errors tag="div" class="form-group" vid="code" rules="required">
                    <label for="code" class="text-normal">Введите полученный код</label>
                    <input id="code" ref="code" v-model="code" v-focus type="text" class="form-control">
                </ValidationProvider>

                <small class="form-text text-muted">
                    Код подтверждения отправлен на ваш email.
                </small>
            </template>

            <template v-if="step === 3">
                <ValidationProvider key="password" v-errors tag="div" class="form-group" vid="password" rules="required">
                    <label for="password" class="text-normal">Новый пароль</label>
                    <input id="password" ref="password" v-model="password" v-focus type="password" class="form-control">
                </ValidationProvider>
            </template>

            <template v-if="lastStep">
                <div class="text-center lead text-success my-4">Готово! Новый пароль установлен.</div>

                <div class="text-center">
                    <router-link :to="{ name: 'login' }" class="btn btn-lg-192 btn-primary">Вход</router-link>
                </div>
            </template>

            <template v-if="!lastStep">
                <div class="my-4">
                    <button :disabled="invalid" class="btn btn-lg-192 btn-primary" type="submit">{{ lastStep ? 'Установить новый пароль' : 'Далее' }}</button>
                </div>

                <div class="d-flex justify-content-between align-items-center">
                    <button class="btn btn-link px-0" type="button" @click="back">Назад</button>
                    <span>Шаг {{ step }} из 3</span>
                </div>
            </template>
        </ValidationObserver>
    </div>
</template>

<script>
import axios from 'axios'
import Recaptcha from '@/components/Recaptcha'

import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { mixinApiErrors } from '@/validation'

export default {
    components: {
        Recaptcha,
        ValidationObserver,
        ValidationProvider
    },

    data() {
        return {
            step: 1,
            identifier: '', // Идентификатор пользователя - почта или номер телефона
            code: '',
            password: '',

            recaptchaToken: ''
        }
    },

    computed: {
        lastStep() {
            return this.step === 4
        },

        isMobile() {
            return this.$store.state.isMobile
        }
    },

    mounted() {
        this.recaptchaTouch('sendPhoneConfirmCode')

        if (this.$route.query.secret) {
            this.step = 3
        }
    },

    methods: {
        /**
         * Колбек готовности капчи, когда она загрузилась и установилась в DOM.
         */
        recaptchaReady() {
            this.recaptchaTouch('step1_sendCode')
        },

        /**
         * Метод "трогает" капчу, чтобы запросить свежий токен.
         */
        recaptchaTouch(action = 'passwordReset') {
            this.recaptchaToken = ''
            this.$refs.recaptcha.execute({ action })
        },

        async submit() {
            if (this.recaptchaToken === false) {
                return
            }

            switch (this.step) {
            case 1:
                return this.step1_sendCode()
            case 2:
                return this.step2_verifyCode()
            case 3:
                return this.step3_setPassword()
            }
        },

        /**
         * Шаг 1. Отправка кода подтверждения
         */
        async step1_sendCode() {
            try {
                await axios.post('api/password-reset/send-code', {
                    identifier: this.identifier
                }, {
                    headers: {
                        'X-ReCaptcha': this.recaptchaToken
                    }
                })

                this.recaptchaTouch('step2_verifyCode')
                this.step = 2

                this.$nextTick(function () {
                    this.$refs.code.focus()
                })
            } catch (error) {
                this.recaptchaTouch('step1_sendCode')
                this.mixinApiErrors(error.response.data || null)
            }
        },

        /**
         * Шаг 2. Проверка введенного кода подтверждения
         */
        async step2_verifyCode() {
            try {
                await axios.post('api/password-reset/verify-code', {
                    identifier: this.identifier,
                    code: this.code
                }, {
                    headers: {
                        'X-ReCaptcha': this.recaptchaToken
                    }
                })

                this.recaptchaTouch('step3_setPassword')
                this.step = 3

                this.$nextTick(function () {
                    this.$refs.password.focus()
                })
            } catch (error) {
                this.recaptchaTouch('step2_verifyCode')
                this.mixinApiErrors(error.response.data || null)
            }
        },

        /**
         * Шаг 3. Установка нового пароля
         */
        async step3_setPassword() {
            try {
                let data = this.$route.query.secret
                    ? {
                        secret: this.$route.query.secret,
                        password: this.password
                    } : {
                        identifier: this.identifier,
                        code: this.code,
                        password: this.password
                    }

                await axios.post('api/password-reset', data, {
                    headers: {
                        'X-ReCaptcha': this.recaptchaToken
                    }
                })

                this.recaptchaTouch('step4')
                this.step = 4
            } catch (error) {
                this.recaptchaTouch('step3_setPassword')
                this.mixinApiErrors(error.response.data || null)
            }
        },

        /**
         * Обработчик кнопки "Назад"
         */
        back() {
            if (this.step > 1) {
                this.step--
            } else {
                this.$router.push({ name: 'login' })
            }
        },

        mixinApiErrors(errors) {
            mixinApiErrors(this.$refs.obs, errors)
        }
    }
}
</script>
